import {
  Badge,
  Button,
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sea-snag/ui';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { useState } from 'react';

import type { MarketFilters } from '../hooks/use-market-filters';
import type { FilterOptions } from '../lib/api-client';
import { KIND_LABELS } from '../lib/boat-kinds';
import { maxMToFt, minMToFt } from '../lib/length-filter';

// What remains here are the small popover-based pickers (SearchableMultiSelect,
// EquipmentFilter) and the active-filter badges row. Both the Comp Set Wizard
// and Market Explorer compose them with WizardFilterPanel, which replaced the
// older range-slider MarketFilterPanel that lived in this file.

export function SearchableMultiSelect({
  label,
  selected,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  selected: string[];
  onChange: (values: string[]) => void;
  options: string[];
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);

  function toggle(opt: string) {
    if (selected.includes(opt)) {
      onChange(selected.filter((v) => v !== opt));
    } else {
      onChange([...selected, opt]);
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-8 text-xs font-normal"
          >
            <span className="truncate">
              {selected.length > 0 ? `${selected.length} selected` : placeholder}
            </span>
            <ChevronDownIcon className="ml-1 size-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[220px] p-0"
          align="start"
          onWheel={(e) => e.stopPropagation()}
        >
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}...`} className="h-8" />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => {
                  const isSelected = selected.includes(opt);
                  return (
                    <CommandItem key={opt} value={opt} onSelect={() => toggle(opt)}>
                      <Checkbox
                        checked={isSelected}
                        className="mr-2 pointer-events-none"
                        tabIndex={-1}
                      />
                      {opt}
                      {isSelected && <CheckIcon className="ml-auto size-3" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((v) => (
            <Badge key={v} variant="secondary" className="gap-1 pr-1 text-xs">
              <span className="max-w-[140px] truncate">{v}</span>
              <button
                type="button"
                onClick={() => onChange(selected.filter((s) => s !== v))}
                className="ml-0.5 rounded-sm opacity-60 hover:opacity-100"
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export function EquipmentFilter({
  equipment,
  selectedIds,
  onChange,
}: {
  equipment: { id: number; name: string }[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const [open, setOpen] = useState(false);

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((i) => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-muted-foreground">Equipment</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-8 text-xs font-normal"
          >
            <span className="truncate">
              {selectedIds.length > 0
                ? `${selectedIds.length} equipment selected`
                : 'Select equipment...'}
            </span>
            <ChevronDownIcon className="ml-1 size-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[280px] p-0"
          align="start"
          onWheel={(e) => e.stopPropagation()}
        >
          <Command>
            <CommandInput placeholder="Search equipment..." className="h-8" />
            <CommandList>
              <CommandEmpty>No equipment found.</CommandEmpty>
              <CommandGroup>
                {equipment.map((g) => {
                  const isSelected = selectedIds.includes(g.id);
                  return (
                    <CommandItem key={g.id} value={g.name} onSelect={() => toggle(g.id)}>
                      <Checkbox
                        checked={isSelected}
                        className="mr-2 pointer-events-none"
                        tabIndex={-1}
                      />
                      {g.name}
                      {isSelected && <CheckIcon className="ml-auto size-3" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedIds.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Boat must have <strong>all</strong> {selectedIds.length} selected
        </p>
      )}
    </div>
  );
}

export function ActiveFilterBadges({
  filters,
  setFilter,
  resetFilters,
  filterOptions,
}: {
  filters: MarketFilters;
  setFilter: <K extends keyof MarketFilters>(key: K, value: MarketFilters[K]) => void;
  resetFilters: () => void;
  filterOptions?: FilterOptions;
}) {
  const badges: { label: string; onRemove: () => void }[] = [];

  // Text/select badges: [displayLabel, filterKey, currentDisplayValue]
  const textBadges: [string, keyof MarketFilters, string][] = [
    ['Type', 'kind', KIND_LABELS[filters.kind] ?? filters.kind],
    ['Base', 'homeBase', filters.homeBase],
    ['Company', 'charterNames', filters.charterNames.join(', ')],
  ];
  for (const [label, key, value] of textBadges) {
    if (value)
      badges.push({
        label: `${label}: ${value}`,
        onRemove: () => setFilter(key, key === 'charterNames' ? ([] as never) : ('' as never)),
      });
  }

  if (filters.models.length > 0)
    badges.push({
      label: `Model: ${filters.models.join(', ')}`,
      onRemove: () => setFilter('models', []),
    });

  // Length is stored in meters as inclusive ft-bin edges; show it in whole feet
  // to match the "Length (ft)" input, using the matching edge inverses (a plain
  // metersToFeet would be off by a foot — see lib/length-filter.ts).
  if (filters.minLength !== undefined || filters.maxLength !== undefined) {
    const lo = filters.minLength !== undefined ? minMToFt(filters.minLength) : '?';
    const hi = filters.maxLength !== undefined ? maxMToFt(filters.maxLength) : '?';
    badges.push({
      label: `Length: ${lo}–${hi} ft`,
      onRemove: () => {
        setFilter('minLength', undefined);
        setFilter('maxLength', undefined);
      },
    });
  }

  // Range badges: [displayLabel, minKey, maxKey, unit]
  const rangeBadges: [string, keyof MarketFilters, keyof MarketFilters, string][] = [
    ['Cabins', 'minCabins', 'maxCabins', ''],
    ['Berths', 'minBerths', 'maxBerths', ''],
    ['Year', 'minYear', 'maxYear', ''],
  ];
  for (const [label, minKey, maxKey, unit] of rangeBadges) {
    const lo = filters[minKey] as number | undefined;
    const hi = filters[maxKey] as number | undefined;
    if (lo !== undefined || hi !== undefined) {
      badges.push({
        label: `${label}: ${lo ?? '?'}${unit}–${hi ?? '?'}${unit}`,
        onRemove: () => {
          setFilter(minKey, undefined as never);
          setFilter(maxKey, undefined as never);
        },
      });
    }
  }

  if (filters.minWc !== undefined)
    badges.push({ label: `WC: ${filters.minWc}+`, onRemove: () => setFilter('minWc', undefined) });

  if (filters.minPeople !== undefined)
    badges.push({
      label: `People: ${filters.minPeople}+`,
      onRemove: () => setFilter('minPeople', undefined),
    });

  if (filters.equipmentIds.length > 0) {
    const equipmentMap = new Map(filterOptions?.equipment?.map((e) => [e.id, e.name]));
    const equipmentNames = filters.equipmentIds
      .map((id) => equipmentMap.get(id) ?? `#${id}`)
      .slice(0, 3);
    const suffix = filters.equipmentIds.length > 3 ? ` +${filters.equipmentIds.length - 3}` : '';
    badges.push({
      label: `Equipment: ${equipmentNames.join(', ')}${suffix}`,
      onRemove: () => setFilter('equipmentIds', []),
    });
  }

  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {badges.map((badge) => (
        <Badge key={badge.label} variant="secondary" className="gap-1 pr-1 text-xs">
          {badge.label}
          <button
            onClick={badge.onRemove}
            className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
          >
            <XIcon className="size-3" />
          </button>
        </Badge>
      ))}
      <Button variant="ghost" size="sm" onClick={resetFilters} className="h-6 text-xs px-2">
        Clear all
      </Button>
    </div>
  );
}
